import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';
import { toast } from 'sonner';
import {
  Loader2, Briefcase, Users, Building2, GraduationCap, UserCheck,
  PlusCircle, ArrowRight, LayoutDashboard, X,
} from 'lucide-react';

const ROLE_META = {
  job_seeker: { label: 'Job Seeker', Icon: Briefcase, tools: [['Browse Jobs', '/jobs'], ['My Dashboard', '/dashboard'], ['Find a Supervisor', '/find-a-supervisor']] },
  supervisor: { label: 'LCSW-S Supervisor', Icon: UserCheck, tools: [['Supervisor Directory', '/find-a-supervisor'], ['Internship Portal', '/internships']] },
  private_practice: { label: 'Private Practice', Icon: Building2, tools: [['Manage My Practice', '/private-practice/list'], ['Practice Directory', '/private-practice'], ['Post a Job', '/employer/post-job'], ['Find a Social Worker', '/for-employers']] },
  employer: { label: 'Employer', Icon: Users, tools: [['Employer Dashboard', '/employer/dashboard'], ['Post a Job', '/employer/post-job'], ['Find a Candidate', '/for-employers']] },
  intern_student: { label: 'Intern / Student', Icon: GraduationCap, tools: [['Find an Internship', '/internships'], ['Find a Supervisor', '/find-a-supervisor']] },
};
const ADD_ORDER = ['job_seeker', 'supervisor', 'private_practice', 'employer', 'intern_student'];
const ROLE_ROUTE_AFTER_ADD = { private_practice: '/private-practice/list', employer: '/employer/post-job', supervisor: '/find-a-supervisor' };

const MyInasw = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try { const res = await axios.get(`${API_URL}/users/roles`); setRoles(res.data.roles || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addRole = async (role) => {
    setAdding(true);
    try {
      const res = await axios.post(`${API_URL}/users/roles`, { role });
      setRoles(res.data.roles || []);
      setShowAdd(false);
      toast.success(`${ROLE_META[role]?.label || role} added to your account.`);
      if (ROLE_ROUTE_AFTER_ADD[role]) navigate(ROLE_ROUTE_AFTER_ADD[role]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add role.');
    } finally { setAdding(false); }
  };

  const available = ADD_ORDER.filter((r) => !roles.includes(r));

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F7F5EF] pt-24 pb-16">
      <Seo exactTitle="My INASW | I Need A Social Worker" description="Your INASW home — all your tools in one place." path="/my-inasw" noindex />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-2">
          <LayoutDashboard className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-3xl font-bold text-primary">My INASW</h1>
        </div>
        <p className="text-gray-500 mb-8">Welcome{user?.name ? `, ${user.name}` : ''}. Everything for your account lives here — one login, all your roles.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {roles.map((role) => {
            const meta = ROLE_META[role];
            if (!meta) return null;
            const { label, Icon, tools } = meta;
            return (
              <div key={role} className="bg-white rounded-2xl border border-gray-100 p-6" data-testid={`role-card-${role}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
                  <h2 className="font-serif text-lg font-bold text-primary">{label}</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tools.map(([t, to]) => (
                    <Link key={to + t} to={to}>
                      <Button variant="outline" size="sm" className="border-primary/30 text-primary hover:bg-primary hover:text-white" data-testid={`tool-${role}-${to}`}>
                        {t} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Add another role card */}
          <button onClick={() => setShowAdd(true)} disabled={available.length === 0}
            className="bg-white rounded-2xl border-2 border-dashed border-secondary/40 p-6 flex flex-col items-center justify-center text-center hover:border-secondary transition-colors disabled:opacity-50"
            data-testid="add-role-card">
            <PlusCircle className="w-8 h-8 text-secondary mb-2" />
            <span className="font-semibold text-primary">Add another role</span>
            <span className="text-xs text-gray-500 mt-1">{available.length ? 'Become a supervisor, list a practice, post jobs & more — no new account.' : 'You have every role.'}</span>
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)} data-testid="add-role-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowAdd(false)} className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            <h3 className="font-serif text-xl font-bold text-primary mb-1">Add a role</h3>
            <p className="text-sm text-gray-500 mb-4">This attaches to your existing account — you'll never need a second login.</p>
            <div className="space-y-2">
              {available.map((r) => {
                const meta = ROLE_META[r];
                const Icon = meta.Icon;
                return (
                  <button key={r} onClick={() => addRole(r)} disabled={adding}
                    className="w-full flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-3 hover:border-primary hover:bg-primary/5 text-left disabled:opacity-50"
                    data-testid={`add-role-${r}`}>
                    <Icon className="w-5 h-5 text-primary" />
                    <span className="font-medium text-gray-800">{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyInasw;
